import {
  Accordion,
  Button,
  Card,
  Checkbox,
  RadioButton,
  TextField,
  toggleExpandedIndexes,
} from '@monash/portal-react';
import c from './get-api-responses.module.scss';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useState } from 'react';
import {
  allApisFalse,
  allApisTrue,
  callableApis,
  getAnonymisedImpersonatee,
  getRedactedImpersonatee,
  getResponses,
  isValidJson,
  transformResponses,
} from './utils';
import { CITIZENSHIP_TYPES } from '../../../../constants/citizenships';
import { useSnackbar } from '../../../providers/SnackbarProvider';
import OutputForm from './output-form/OutputForm';

const GetApiResponses = () => {
  const [apiResponses, setApiResponses] = useState();
  const [mockUserIdentity, setMockUserIdentity] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const showOutputForm = apiResponses && mockUserIdentity;

  const { addSnackbar } = useSnackbar();

  const methods = useForm({
    defaultValues: {
      selectedApis: allApisTrue,
      shouldFallbackForUnselected: true,
      unselectedFallback: 'null',
      errorFallback: 'null',
      fakeIdentity: {
        username: 'jdoe0000',
        firstName: 'John',
        lastName: 'Doe',
        preferredName: 'John',
        studentId: '00000000',
        phoneNumber: '0000 000 000',
        pronouns: 'He/Him/His',
        citizenshipType: 'Domestic',
      },
    },
  });
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;

  const watchedShouldFallback = useWatch({
    control,
    name: 'shouldFallbackForUnselected',
  });
  const watchedSelectedApis = useWatch({ control, name: 'selectedApis' });
  const isAllSelected = Object.values(watchedSelectedApis).every(Boolean);

  const handleCheckAll = () => {
    isAllSelected
      ? setValue('selectedApis', allApisFalse)
      : setValue('selectedApis', allApisTrue);
  };

  const onSubmit = async (values) => {
    const {
      selectedApis,
      unselectedFallback,
      shouldFallbackForUnselected,
      errorFallback,
      fakeIdentity,
    } = values;

    setIsLoading(true);
    setApiResponses('');

    try {
      const [redactedImpersonatee, responses] = await Promise.all([
        getRedactedImpersonatee(),
        getResponses({
          selectedApis,
          shouldFallbackForUnselected,
          unselectedFallback: JSON.parse(unselectedFallback),
          errorFallback: JSON.parse(errorFallback),
        }),
      ]);

      if (!redactedImpersonatee) {
        addSnackbar({
          message: 'Your impersonation session has expired. Please refresh',
          type: 'error',
        });
        return;
      }

      const transformedResponses = transformResponses({
        responses,
        redactedImpersonatee,
        fakeIdentity,
        selectedApis,
      });

      const mockUserIdentity = getAnonymisedImpersonatee({
        fakeIdentity,
        redactedImpersonatee,
      });

      setApiResponses(transformedResponses);
      setMockUserIdentity(mockUserIdentity);
      setOpenAccordionIndexes([]);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onFormSubmitError = (error) => console.error(error);

  const formSections = [
    {
      title: 'Selected APIs',
      content: (
        <div className={c.checkboxList}>
          <Checkbox
            on={isAllSelected}
            onChange={handleCheckAll}
            text="Select all"
          />
          {Object.keys(callableApis).map((apiName) => {
            return (
              <Controller
                key={apiName}
                name={`selectedApis.${apiName}`}
                control={control}
                render={({ field: { value, onChange, onBlur } }) => (
                  <Checkbox
                    text={apiName}
                    on={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            );
          })}
        </div>
      ),
    },
    {
      title: 'Anonymise/customise identity',
      content: (
        <div className={c.sectionContainer}>
          <div className={c.inputContainer}>
            <h3>Username</h3>
            <Controller
              name="fakeIdentity.username"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  placeholder="Username"
                  error={() => errors.fakeIdentity?.username}
                  errorMsg={errors.fakeIdentity?.username?.message}
                />
              )}
            />
          </div>
          <div className={c.inputContainer}>
            <h3>First name</h3>
            <Controller
              name="fakeIdentity.firstName"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  placeholder="First name"
                  error={() => errors.fakeIdentity?.firstName}
                  errorMsg={errors.fakeIdentity?.firstName?.message}
                />
              )}
            />
          </div>
          <div className={c.inputContainer}>
            <h3>Last name</h3>
            <Controller
              name="fakeIdentity.lastName"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  placeholder="Last name"
                  error={() => errors.fakeIdentity?.lastName}
                  errorMsg={errors.fakeIdentity?.lastName?.message}
                />
              )}
            />
          </div>
          <div className={c.inputContainer}>
            <h3>Preferred name</h3>
            <Controller
              name="fakeIdentity.preferredName"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  placeholder="Preferred name"
                  error={() => errors.fakeIdentity?.preferredName}
                  errorMsg={errors.fakeIdentity?.preferredName?.message}
                />
              )}
            />
          </div>
          <div className={c.inputContainer}>
            <h3>Student ID</h3>
            <Controller
              name="fakeIdentity.studentId"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  placeholder="Student ID"
                  error={() => errors.fakeIdentity?.studentId}
                  errorMsg={errors.fakeIdentity?.studentId?.message}
                />
              )}
            />
          </div>
          <div className={c.inputContainer}>
            <h3>Phone number</h3>
            <Controller
              name="fakeIdentity.phoneNumber"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  placeholder="Phone number"
                  error={() => errors.fakeIdentity?.phoneNumber}
                  errorMsg={errors.fakeIdentity?.phoneNumber?.message}
                />
              )}
            />
          </div>
          <div className={c.inputContainer}>
            <h3>Pronouns</h3>
            <Controller
              name="fakeIdentity.pronouns"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  placeholder="Pronouns"
                  error={() => errors.fakeIdentity?.pronouns}
                  errorMsg={errors.fakeIdentity?.pronouns?.message}
                />
              )}
            />
          </div>
          <div className={c.inputContainer}>
            <h3>Citizenship type</h3>
            <div>
              <Controller
                name="fakeIdentity.citizenshipType"
                control={control}
                render={({ field: { value, onChange } }) =>
                  Object.entries(CITIZENSHIP_TYPES).map(
                    ([key, citizenship]) => (
                      <RadioButton
                        key={key}
                        label={citizenship}
                        onChange={() => onChange(citizenship)}
                        on={value === citizenship}
                      />
                    )
                  )
                }
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Fallbacks',
      content: (
        <div className={c.sectionContainer}>
          <div className={c.inputContainer}>
            <h3>Error fallback response</h3>
            <Controller
              name="errorFallback"
              control={control}
              rules={{
                validate: {
                  isValidJson,
                },
              }}
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  placeholder="Enter some JSON here"
                  multiline
                  error={() => errors.errorFallback}
                  errorMsg={errors.errorFallback?.message}
                />
              )}
            />
          </div>

          <div className={c.inputContainer}>
            <h3>Populate unselected APIs with a fallback response?</h3>
            <div className={c.radioGroup}>
              <Controller
                name="shouldFallbackForUnselected"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <>
                    <RadioButton
                      label="Yes"
                      onChange={() => onChange(true)}
                      on={value}
                    />
                    <RadioButton
                      label="No"
                      onChange={() => onChange(false)}
                      on={!value}
                    />
                  </>
                )}
              />
            </div>
          </div>
          {watchedShouldFallback && (
            <div className={c.inputContainer}>
              <h3>Unselected fallback response</h3>
              <Controller
                name="unselectedFallback"
                control={control}
                rules={{
                  validate: {
                    isValidJson,
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    placeholder="Enter some JSON here"
                    multiline
                    error={() => errors.unselectedFallback}
                    errorMsg={errors.unselectedFallback?.message}
                  />
                )}
              />
            </div>
          )}
        </div>
      ),
    },
  ];

  const [openAccordionIndexes, setOpenAccordionIndexes] = useState(
    formSections.map((_, i) => i)
  );

  return (
    <div className={c.apiResponses}>
      <h2>Get API Responses</h2>
      <div>
        These responses are tweaked to give an anonymised representation of a
        particular type of student. It is <b>not</b> a literal one-to-one
        mapping of real responses to API calls.
      </div>
      <Card className={c.formContainer}>
        <form
          className={c.form}
          onSubmit={handleSubmit(onSubmit, onFormSubmitError)}
        >
          <Accordion
            renderHiddenContent
            expandedIndexes={openAccordionIndexes}
            onClick={(index) =>
              setOpenAccordionIndexes(
                toggleExpandedIndexes(index, openAccordionIndexes)
              )
            }
            items={formSections}
          />
          <div className={c.buttonsContainer}>
            <Button
              className={c.submitButton}
              type="submit"
              loading={isLoading}
            >
              Get responses
            </Button>
          </div>
        </form>
        <div className={c.outputsContainer}>
          <h2>Outputs</h2>
          {showOutputForm ? (
            <OutputForm
              apiResponses={apiResponses}
              mockUserIdentity={mockUserIdentity}
            />
          ) : (
            <div>Output will be shown here</div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default GetApiResponses;
